import React, { useState, useRef, useEffect } from "react";
import FormInput from "components/Inputs/FormInput";
import "./DocUploader.css";
import { t } from "i18next";
import { humanFileSize } from "includes/documents_treat";
import DocumentObject from "classes/Documents/DocumentObject";
import ErrorsCont from "components/ErrorsCont/ErrorsCont";
import SwitchInput from "components/Inputs/Switch/SwitchInput";
import PageLoad from "components/PageLoad/PageLoad";
import DocumentTile from "../Tile/DocumentTile";
import generateUUID from "includes/UUID";
import Button from "components/Button/Button";
import CategoriesSelect from "components/CustomSelects/CategoriesSelect";
import useAccount from "classes/Accounts/hooks/useAccount";
import { TypeRights, returnRightsFromName } from "includes/rightType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowDown, faPlus, faTrash, faTimes } from "@fortawesome/free-solid-svg-icons";

const useSingleDocUploader = (props, uploaderId) => {
	const defaultMaxSize = 20 * 1024 * 1024;
	const inputRef = useRef(null);

	const [file, setFile] = useState(false);
	const [error, setError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [doc, setDoc] = useState(null);
	const [needsUpdate, setNeedsUpdate] = useState(0);

	useEffect(() => {
		if (props.doc) {
			setDoc(props.doc);
			if (props.doc.file()) {
				setFile(props.doc.file());
			}
		}
		else if (!doc) {
			const newDoc = new DocumentObject({
				DocTypeId: props.typeId,
				CompanyId: props.companyId,
				EmployeId: props.employesId,
				IsNew: true,
				IsTargetDoc: true,
			});
			setDoc(newDoc);
		}
	}, [props.doc]);

	const handleFile = (event) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		const selectedFile = files[0];
		setFile(null);
		setError(null);

		const max = props.maxSize || defaultMaxSize;
		if (selectedFile.size > max) {
			setError(
				`${t("Files.FILE_SIZE_ERROR")} (${humanFileSize(selectedFile.size)}) ` +
				`${t("Files.FILE_SIZE_ERROR_2")} ${humanFileSize(max)}`
			);
			event.target.value = "";
			return;
		}

		setIsLoading(true);
		const reader = new FileReader();

		reader.onload = async (e) => {
			const dataUrl = e.target.result;
			const properFileObject = await dataURLtoFileFetch(dataUrl, selectedFile.name);
			setFile(properFileObject);

			doc.file(properFileObject);
			doc.documentFile(dataUrl.split(",")[1]);
			doc.urlOrigin(dataUrl);
			doc.name(selectedFile.name.replace(/\.[^/.]+$/, ""));

			props.onDocChange?.(uploaderId, doc);
			event.target.value = "";
			setIsLoading(false);
			triggerUpdate();
		};

		reader.readAsDataURL(selectedFile);
	};

	const handleCategorySelect = (values) => {
		doc.categories(
			values.map(a => ({
				CategoryId: !a.isNew ? a.value : false,
				DefaultHexaColor: a.color,
				Name: a.label,
				isNew: !!a.isNew,
			}))
		);
		props.onDocChange?.(uploaderId, doc);
		triggerUpdate();
	};

	const modifyFile = () => inputRef.current?.click();
	const updateDocName = (name) => {
		doc.name(name);
		props.onDocChange?.(uploaderId, doc);
		triggerUpdate();
	};
	const triggerUpdate = () => setNeedsUpdate(prev => prev + 1);

	return {
		states: { file, error, isLoading, doc },
		refs: { inputRef },
		actions: { handleFile, handleCategorySelect, modifyFile, updateDocName, triggerUpdate }
	};
};

async function dataURLtoFileFetch(dataUrl, filename) {
	const res = await fetch(dataUrl);
	const blob = await res.blob();
	return new File([blob], filename, { type: blob.type });
}

export const SingleDocUploader = ({ uploaderId, onRemove, showRemove, ...props }) => {
	const {
		states: { file, error, isLoading, doc },
		refs: { inputRef },
		actions: { handleFile, handleCategorySelect, modifyFile, updateDocName, triggerUpdate }
	} = useSingleDocUploader(props, uploaderId);

	return (
		<div className={`single-doc-uploader border rounded p-3 mb-3 ${props.className || ""}`}>
			<div className="mb-3">
				{showRemove && (
					<div className="single-doc-uploader-header">
						<h5 className="mb-0">{t("Files.DOCUMENT")} {uploaderId + 1}</h5>
						<Button className="btn btn-sm btn-outline-danger" onClick={() => onRemove(uploaderId)}>
							<FontAwesomeIcon icon={faTrash} />
						</Button>
					</div>
				)}
			</div>
			<div className="mb-3">
				<div className="mb-2">
					{file && <Button className="btn btn-sm btn-blue" onClick={modifyFile}>{t("Commons.MODIFY")}</Button>}
				</div>
				<div className={`drop-cont upload-container ${file ? "d-none" : "d-flex"} ${error && !file ? "error" : ""} justify-content-center align-items-center`} style={{ minHeight: "120px" }}>
					{isLoading && <div>{t("Files.FILE_LOAD")}</div>}
					{!isLoading && <div className="placeholder">{t("Files.FILE_PH")}</div>}
					<input ref={inputRef} className="file-input" accept="application/pdf" type="file" onChange={handleFile} />
					<div className="icon" style={{ color: props.primaryColor }}><FontAwesomeIcon icon={faCircleArrowDown} /></div>
				</div>
				{file && (file instanceof Blob || file instanceof File) && <DocPreview noPreview={props.noPreviews} file={file} url={URL.createObjectURL(file)} name={file.name} />}
			</div>
			<ErrorsCont className="mb-3" errors={error} />
			<div className="mb-3">
				{file && <FormInput className="w-100" placeholder={t("Files.FILE_NAME")} value={file?.name} onChange={updateDocName} />}
			</div>
			{props.selCats !== false && (
				<CategoriesSelect noTitle={true} className="mb-3" type={"documents"} companyId={props.companyId} initialValue={doc ? doc.categories() : false} onSelect={handleCategorySelect} />
			)}
			{props.docsUtilImport === true && doc && <DocUpload doc={doc} onStateChange={triggerUpdate} />}
		</div>
	);
};

const DocUploader = (props) => {
	const [uploaders, setUploaders] = useState([0]);
	const [docs, setDocs] = useState({});
	const [globalError, setGlobalError] = useState(false);

	const addUploader = () => {
		const newId = (uploaders.length > 0 ? Math.max(...uploaders) : -1) + 1;
		setUploaders(prev => [...prev, newId]);
	};

	const removeUploader = (uploaderId) => {
		if (uploaders.length <= 1) return;

		const updatedDocs = { ...docs };
		delete updatedDocs[uploaderId];
		const updatedDocsArray = Object.values(updatedDocs);

		setUploaders(prev => prev.filter(id => id !== uploaderId));
		setDocs(updatedDocs);

		props.onConfirm(updatedDocsArray);
	};

	const handleDocChange = (uploaderId, doc) => {
		const newDocs = { ...docs, [uploaderId]: doc };
		setDocs(newDocs);
		props.onChange?.(Object.values(newDocs));

		if (!props.onConfirm) return;
		props.onConfirm(Object.values(newDocs));
	};

	return (
		<div className={`DocUploader multi-upload ${props.className || ""}`}>
			<div className="d-flex justify-content-between align-items-center mb-4">
				<h2>{t("Files.IMPORT_FROM_PC")}</h2>
			</div>
			{uploaders.map((uploaderId, index) => (
				<SingleDocUploader
					key={uploaderId}
					uploaderId={uploaderId}
					onRemove={removeUploader}
					showRemove={uploaders.length > 1}
					onDocChange={handleDocChange}
					{...props}
				/>
			))}
			<ErrorsCont className="mb-4" errors={globalError} />
			<div className="mt-3">
				<Button className="btn btn-outline-primary" onClick={addUploader}>
					<FontAwesomeIcon icon={faPlus} className="me-2" />
					{t("Files.ADD_DOCUMENT") || "Ajouter un document"}
				</Button>
			</div>
		</div>
	);
};

const DocUpload = ({ doc, onStateChange }) => {
	const { account } = useAccount();
	const checkRight = () => {
		try { return returnRightsFromName(account.Rights, TypeRights.AdminDocument); }
		catch (error) { return true; }
	};

	const handleChange = (newValue) => {
		doc.addToDocsUtiles(newValue);
		onStateChange();
	};

	return (
		checkRight() && (
			<div className="mb-4">
				<SwitchInput
					label={t("Docs.ADD_TO_DOCS_UTIL")}
					onlyState={true}
					checked={doc.addToDocsUtiles()}
					onChange={handleChange}
				/>
			</div>
		)
	);
};

const DocPreview = ({ url, file, name, noPreview = false, onRemove }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	if (!url) return null;
	return (
		<div className="DocPreview position-relative">
			{onRemove && (
				<button className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1" onClick={onRemove} style={{ zIndex: 10 }}>
					<FontAwesomeIcon icon={faTimes} />
				</button>
			)}
			<DocumentTile doc={{ Name: name, DocumentId: generateUUID() }} onClick={() => { }} handleDelete={false} handleSave={false} categories={false} size={file.size} className="pe-none" />
			{file?.size < 2000000 ? (
				<>
					{!noPreview && <iframe allow="fullscreen" title={name} src={url} className="mt-2 w-100" onLoad={() => setIsLoaded(true)} />}
					{/* {!isLoaded && <div className="loading-frame"><PageLoad text={t("Commons.PREVIEW_LOAD")} /></div>} */}
				</>
			) : (
				<div className="mt-2 w-100 text-center too-heavy-msg">{t("Docs.PREVIEW_SIZE_ERROR")}</div>
			)}
		</div>
	);
};

export default DocUploader;